import { ProductType as CourseType } from "@/common/course/types";
import { ProgramType } from "@/common/program/types";

export type ValuePropKeys = {
  icon: string;
  titleKey: string;
  descriptionKey: string;
};

export type TabContentKeys = {
  titleKey: string;
  subtitleKey: string;
  data: ValuePropKeys[];
};

type Translate = (key: string) => string;

export const productTabContentKeys: Record<string, TabContentKeys> = {
  [ProgramType.CERTIFICATE]: {
    titleKey: "certificateTitle",
    subtitleKey: "certificateSubtitle",
    data: [
      {
        icon: "check-circle",
        titleKey: "standOutTitle",
        descriptionKey: "standOutDescription",
      },
      {
        icon: "clock",
        titleKey: "learnAtYourOwnPaceTitle",
        descriptionKey: "learnAtYourOwnPaceDescription",
      },
      {
        icon: "trending-up",
        titleKey: "earnCredentialTitle",
        descriptionKey: "earnCredentialDescription",
      },
    ],
  },
  [CourseType.COURSE]: {
    titleKey: "courseTitle",
    subtitleKey: "courseSubtitle",
    data: [
      {
        icon: "check-circle",
        titleKey: "standOutTitle",
        descriptionKey: "standOutDescription",
      },
      {
        icon: "clock",
        titleKey: "learnAtYourOwnPaceTitle",
        descriptionKey: "learnAtYourOwnPaceDescription",
      },
      {
        icon: "trending-up",
        titleKey: "earnCredentialTitle",
        descriptionKey: "earnCredentialDescription",
      },
    ],
  },
  [CourseType.EXECUTIVE_EDUCATION]: {
    titleKey: "executiveEducationTitle",
    subtitleKey: "executiveEducationSubtitle",
    data: [
      {
        icon: "check-circle",
        titleKey: "outcomesBasedTitle",
        descriptionKey: "outcomesBasedDescription",
      },
      {
        icon: "trending-points",
        titleKey: "realWorldImpactTitle",
        descriptionKey: "realWorldImpactDescription",
      },
      {
        icon: "customer-service",
        titleKey: "dedicatedSupportTitle",
        descriptionKey: "dedicatedSupportDescription",
      },
    ],
  },
  [ProgramType.MASTERS]: {
    titleKey: "mastersTitle",
    subtitleKey: "mastersSubtitle",
    data: [
      {
        icon: "check-circle",
        titleKey: "prestigiousTitle",
        descriptionKey: "prestigiousMastersDescription",
      },
      {
        icon: "clock",
        titleKey: "flexibleTitle",
        descriptionKey: "flexibleDescription",
      },
      {
        icon: "trending-up",
        titleKey: "equivalentToOnCampusTitle",
        descriptionKey: "equivalentToOnCampusDescription",
      },
    ],
  },
  [ProgramType.BACHELORS]: {
    titleKey: "bachelorsTitle",
    subtitleKey: "bachelorsSubtitle",
    data: [
      {
        icon: "check-circle",
        titleKey: "prestigiousTitle",
        descriptionKey: "prestigiousBachelorsDescription",
      },
      {
        icon: "clock",
        titleKey: "onlineFlexibilityTitle",
        descriptionKey: "onlineFlexibilityDescription",
      },
      {
        icon: "people",
        titleKey: "collaborativeConnectionsTitle",
        descriptionKey: "collaborativeConnectionsDescription",
      },
    ],
  },
  [ProgramType.DOCTORATE]: {
    titleKey: "doctorateTitle",
    subtitleKey: "doctorateSubtitle",
    data: [
      {
        icon: "check-circle",
        titleKey: "prestigiousTitle",
        descriptionKey: "prestigiousDoctorateDescription",
      },
      {
        icon: "clock",
        titleKey: "flexibleTitle",
        descriptionKey: "flexibleDescription",
      },
      {
        icon: "trending-up",
        titleKey: "equivalentToOnCampusTitle",
        descriptionKey: "equivalentToOnCampusDescription",
      },
    ],
  },
};

/** Icon-list defaults reuse the same value-prop copy as tabs-by-topic. */
export const productIconListKeys: Record<string, { data: ValuePropKeys[] }> = {
  [CourseType.COURSE]: {
    data: productTabContentKeys[CourseType.COURSE].data,
  },
  [CourseType.EXECUTIVE_EDUCATION]: {
    data: productTabContentKeys[CourseType.EXECUTIVE_EDUCATION].data,
  },
  [ProgramType.MASTERS]: {
    data: productTabContentKeys[ProgramType.MASTERS].data,
  },
  [ProgramType.BACHELORS]: {
    data: productTabContentKeys[ProgramType.BACHELORS].data,
  },
};

export const localizeValueProps = (data: ValuePropKeys[], t: Translate) =>
  data.map((item) => ({
    icon: item.icon,
    title: t(item.titleKey),
    description: t(item.descriptionKey),
  }));

export const localizeTabDefaults = (productType: string, t: Translate) => {
  const keys = productTabContentKeys[productType];
  if (!keys) {
    return { title: "", subtitle: "", data: [] };
  }
  return {
    title: t(keys.titleKey),
    subtitle: t(keys.subtitleKey),
    data: localizeValueProps(keys.data, t),
  };
};
